import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Package, Search, Eye, AlertCircle, Download } from "lucide-react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useNavigate } from "react-router-dom";
import Button from "shared/Button";
import {
  getInventory,
  getInventoryStatistics,
  exportInventory,
  InventoryItem,
  InventoryStatistics,
} from "core/Services/InventoryServices";
import { USER_ID } from "utils/constant";

const MyInventory = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [statistics, setStatistics] = useState<InventoryStatistics | null>(
    null
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>("");

  const merchantId = localStorage.getItem(USER_ID);

  useEffect(() => {
    const fetchDashboard = async () => {
      if (!merchantId) return;

      setIsLoading(true);
      try {
        const response = await getInventory(
          parseInt(merchantId),
          {
            search: searchTerm,
            status: statusFilter || undefined,
            per_page: 50,
          },
          currentPage
        );
        console.log("Pagination response:", response);

        // Handle both direct pagination and resource collection response
        const items = response.data || [];
        const lastPage = response.last_page || response.meta?.last_page || 1;
        const totalCount = response.total || response.meta?.total || 0;

        setInventoryItems(Array.isArray(items) ? items : []);
        setTotalPages(lastPage);
        setTotal(totalCount);
      } catch (error) {
        console.error("Error fetching inventory:", error);
        toast.error(t("inventory.errorFetchingInventory"));
        setInventoryItems([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboard();
  }, [merchantId, searchTerm, statusFilter, currentPage, t]);

  useEffect(() => {
    const fetchStatistics = async () => {
      if (!merchantId) return;

      try {
        const stats = await getInventoryStatistics(parseInt(merchantId));
        setStatistics(stats);
      } catch (error) {
        console.error("Error fetching statistics:", error);
      }
    };

    fetchStatistics();
  }, [merchantId]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    console.log("Page change requested:", page);
    setCurrentPage(page);
  };

  const handleExport = async () => {
    if (!merchantId) {
      toast.error(t("inventory.merchantIdRequired") || "Merchant ID required");
      return;
    }

    setIsExporting(true);
    try {
      await exportInventory(parseInt(merchantId));
      toast.success(
        t("inventory.exportSuccess") || "Inventory exported successfully"
      );
    } catch (error) {
      console.error("Error exporting inventory:", error);
      toast.error(t("inventory.exportError") || "Failed to export inventory");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="flex gap-4 flex-col p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="bg-white rounded-md shadow-sm p-4 sm:p-6 flex gap-4 flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <Package size={32} className="text-primary" />
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                {t("inventory.myInventory")}
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                {t("inventory.viewYourProducts")}
              </p>
            </div>
          </div>
          <Button
            onClick={handleExport}
            disabled={isExporting || isLoading || inventoryItems.length === 0}
            className="flex items-center gap-2"
            icon={<Download size={18} />}
          >
            {isExporting
              ? t("inventory.exporting") || "Exporting..."
              : t("inventory.export") || "Export"}
          </Button>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-600 font-medium">
                {t("inventory.totalItems") || "Total Records"}
              </p>
              <p className="text-2xl font-bold text-blue-900 mt-1">
                {statistics.total_items}
              </p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <p className="text-sm text-yellow-600 font-medium">
                {t("inventory.underReview") || "Under Review"}
              </p>
              <p className="text-2xl font-bold text-yellow-900 mt-1">
                {statistics.under_review}
              </p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-sm text-green-600 font-medium">
                {t("inventory.received") || "Received"}
              </p>
              <p className="text-2xl font-bold text-green-900 mt-1">
                {statistics.received}
              </p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <p className="text-sm text-red-600 font-medium">
                {t("inventory.missingQuantity") || "Missing Quantity"}
              </p>
              <p className="text-2xl font-bold text-red-900 mt-1">
                {statistics.missing_quantity}
              </p>
            </div>
          </div>
        )}

        {/* Search Box */}
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1 max-w-md">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={20}
              />
              <input
                type="text"
                placeholder={t("inventory.searchBySku")}
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div className="w-full sm:w-48">
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">
                  {t("inventory.allStatus") || "All Status"}
                </option>
                <option value="under_review">
                  {t("inventory.underReview") || "Under Review"}
                </option>
                <option value="received">
                  {t("inventory.received") || "Received"}
                </option>
                <option value="missing_quantity">
                  {t("inventory.missingQuantity") || "Missing Quantity"}
                </option>
              </select>
            </div>
          </div>
        </div>

        {/* Dashboard Table */}
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left p-3 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("inventory.sku")}
                </th>
                <th className="text-left p-3 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("inventory.productName")}
                </th>
                <th className="text-left p-3 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("inventory.manufacturer")}
                </th>
                <th className="text-left p-3 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("inventory.importDate") || "Import Date"}
                </th>
                <th className="text-left p-3 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("inventory.originalQuantity") || "Original Qty"}
                </th>
                <th className="text-left p-3 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("inventory.available") || "Available"}
                </th>
                <th className="text-left p-3 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("inventory.missing") || "Missing"}
                </th>
                <th className="text-left p-3 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("inventory.price")}
                </th>
                <th className="text-left p-3 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("inventory.status") || "Status"}
                </th>
                <th className="text-right p-3 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("inventory.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                Array(5)
                  .fill(0)
                  .map((_, index) => (
                    <tr key={index}>
                      <td className="py-4 px-3">
                        <Skeleton width={100} />
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={150} />
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={100} />
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={120} />
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={60} />
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={60} />
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={60} />
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={60} />
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={80} />
                      </td>
                      <td className="py-4 px-3 text-right">
                        <Skeleton width={20} />
                      </td>
                    </tr>
                  ))
              ) : inventoryItems.length > 0 ? (
                inventoryItems.map((item) => (
                  <tr
                    key={item.id}
                    className={`hover:bg-gray-50 transition-colors ${
                      item.quantity_status === "under_review"
                        ? "bg-yellow-50"
                        : ""
                    }`}
                  >
                    <td className="py-4 px-3">
                      <span className="font-mono font-medium text-sm">
                        {item.sku}
                      </span>
                    </td>
                    <td className="py-4 px-3">
                      <div>
                        <p className="font-medium text-sm">
                          {item.product_name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {item.product_code}
                        </p>
                      </div>
                    </td>
                    <td className="py-4 px-3 text-gray-600 text-sm">
                      {item.manufacturer || "-"}
                    </td>
                    <td className="py-4 px-3 text-gray-600 text-sm">
                      {new Date(
                        item.import_date || item.created_at
                      ).toLocaleDateString()}
                    </td>
                    <td className="py-4 px-3">
                      <span className="font-semibold text-gray-800">
                        {item.quantity}
                      </span>
                    </td>
                    <td className="py-4 px-3">
                      <span className="font-semibold text-green-600">
                        {item.available_quantity}
                      </span>
                    </td>
                    <td className="py-4 px-3">
                      <span className="font-semibold text-red-600">
                        {item.missing_quantity_number || 0}
                      </span>
                    </td>
                    <td className="py-4 px-3 text-gray-600">
                      JOD {item.price.toFixed(2)}
                    </td>
                    <td className="py-4 px-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          item.quantity_status === "received"
                            ? "bg-green-100 text-green-800"
                            : item.quantity_status === "under_review"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {item.quantity_status.replace("_", " ")}
                      </span>
                    </td>
                    <td className="py-4 px-3 text-right">
                      <span className="text-gray-400 text-xs">Read-only</span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={10}
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    {t("inventory.noImportsFound") || "No import records found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="bg-white px-4 py-3 flex flex-col sm:flex-row items-center justify-between border-t border-gray-200 sm:px-6 space-y-4 sm:space-y-0">
          <div className="text-sm text-gray-500">
            {!isLoading && total > 0 ? (
              <>
                {t("inventory.showing")} {(currentPage - 1) * 50 + 1}-
                {Math.min(currentPage * 50, total)} {t("inventory.of")} {total}{" "}
                {t("inventory.entries")}
              </>
            ) : (
              <span>{t("inventory.noImportsFound") || "No records"}</span>
            )}
          </div>
          <div className="flex space-x-1">
            <button
              className="px-3 py-1 border rounded text-sm disabled:opacity-50 hover:bg-gray-50 transition-colors"
              disabled={currentPage === 1 || isLoading}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              {t("inventory.previous")}
            </button>
            <button className="px-3 py-1 border rounded text-sm bg-blue-600 text-white">
              {currentPage} / {totalPages || 1}
            </button>
            <button
              className="px-3 py-1 border rounded text-sm disabled:opacity-50 hover:bg-gray-50 transition-colors"
              disabled={
                currentPage === totalPages || totalPages <= 1 || isLoading
              }
              onClick={() => handlePageChange(currentPage + 1)}
            >
              {t("inventory.next")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyInventory;
